import { adminConstants, employerConstants } from "./types";
import {
  approvePayrollIntegrationSetup,
  createCustomReport as createCustomReportRequest,
  createInvoiceForCompany,
  createOrganization,
  disableCompanyRequest,
  enableCompanyRequest,
  enableFeatureForCompany,
  enableFeaturesForCompany,
  getAllUnprocessedPayrollsRequest,
  getApprovalRequest as getApprovalById,
  getApprovalRequests,
  getBillingDetailsForCompany,
  getComplianceReports,
  getCustomReports,
  getEmployerCrmLinkRequest,
  getEmployerGroupsByCompanyIdRequest,
  getEntityLimitsRequest,
  getGroupsAndBanks,
  getKybStatus,
  getKybStatusForCompany,
  getLastWorkflowStep,
  getOrgForCompany,
  getPaginatedCompanies as getCompaniesSet,
  getPayrolls,
  getPendingAndLinkedEmployers,
  getPricingSheet,
  getProductFeatures,
  getReportsStatuses,
  getReportTypes,
  getUserDetails,
  getUserReviewDocuments,
  getEmployerOrders,
  getUserRollovers as userRollovers,
  getUserTransactions,
  getWorkflows,
  linkBank,
  linkEmployerToCrmRequest,
  liquidateUserAccount,
  lockUser,
  migrateEmployeesForCompany,
  partiallyDisburseUserAccount,
  paychexUsersReport,
  reopenUserAccount,
  requirePortfolioUpdateRequest,
  resetUserState,
  restartWorkflowRequest,
  searchUsers,
  setApproval,
  submitKYB,
  submitKYBBusinessDocuments,
  submitKYBMemberDocuments,
  submitKYBUser,
  syncCrmRequest,
  unlinkEmployerToCrmRequest,
  unlockUser,
  updateBillingContact,
  updateBillingType,
  updateCompanyInfo as updateCompanyInfoNonKyb,
  updateCompanyKYBInfo,
  updateEntityLimitRequest,
  uploadEmployeesDocumentForEmployerRequest,
  getPaychexUsers,
  requestBusinessRegistrationUpdate,
  updateUserEmail as updateUserEmailService,
  updateUserKyc as updateUserKycService,
  adminUpdateUserProfile as adminUpdateUserProfileService,
  getAlloyApplication,
} from "services/adminService";
import type { ApolloClient } from "@apollo/client";
import type { Dispatch } from "redux";

import ActionCreator from "utils/actionHandler";

export const registerKYBUser = (
  client: ApolloClient<object>,
  kybUser: Parameters<typeof submitKYBUser>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitKYBUser>>
    >(adminConstants.REGISTER_KYB_USER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitKYBUser(client, kybUser)
    );
  };
};

export const registerKYB = (
  client: ApolloClient<object>,
  kybEmployer: Parameters<typeof submitKYB>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitKYB>>
    >(adminConstants.REGISTER_KYB);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitKYB(client, kybEmployer)
    );
  };
};

export const getApproval = (
  client: ApolloClient<object>,
  approvalId: Parameters<typeof getApprovalById>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getApprovalById>>
    >(adminConstants.GET_APPROVAL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getApprovalById(client, approvalId)
    );
  };
};

export const setApprovalStatus = (
  client: ApolloClient<object>,
  approvalParams: Parameters<typeof setApproval>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof setApproval>>
    >(adminConstants.SET_APPROVAL_STATUS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      setApproval(client, approvalParams)
    );
  };
};

export const getReports = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getComplianceReports>>
    >(adminConstants.GET_COMPLIANCE_REPORTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getComplianceReports(client)
    );
  };
};

export const getPendingApprovals = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getApprovalRequests>>
    >(adminConstants.GET_PENDING_APPROVALS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getApprovalRequests(client, "PENDING")
    );
  };
};

export const getUserAccountReviewDocuments = (
  client: ApolloClient<object>,
  userId: Parameters<typeof getUserReviewDocuments>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getUserReviewDocuments>>
    >(adminConstants.GET_USER_ACCOUNT_REVIEW_DOCUMENTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getUserReviewDocuments(client, userId)
    );
  };
};

export const enableFeature = (
  client: ApolloClient<object>,
  params: Parameters<typeof enableFeatureForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof enableFeatureForCompany>>
    >(adminConstants.ENABLE_FEATURE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      enableFeatureForCompany(client, params)
    );
  };
};

export const getEmployerGroupsByCompanyId = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof getEmployerGroupsByCompanyIdRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getEmployerGroupsByCompanyIdRequest>>
    >(adminConstants.GET_GROUPS_BY_COMPANY_ID);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getEmployerGroupsByCompanyIdRequest(client, companyId)
    );
  };
};

export const getAllProductFeatures = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getProductFeatures>>
    >(adminConstants.GET_PRODUCT_FEATURES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getProductFeatures(client)
    );
  };
};

export const enableProductFeatures = (
  client: ApolloClient<object>,
  params: Parameters<typeof enableFeaturesForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof enableFeaturesForCompany>>
    >(adminConstants.ENABLE_PRODUCT_FEATURES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      enableFeaturesForCompany(client, params)
    );
  };
};

export const uploadEmployeesDocumentForEmployer = (
  client: ApolloClient<object>,
  groupId: Parameters<typeof uploadEmployeesDocumentForEmployerRequest>[1],
  file: Parameters<typeof uploadEmployeesDocumentForEmployerRequest>[2],
  companyId: Parameters<typeof uploadEmployeesDocumentForEmployerRequest>[3]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof uploadEmployeesDocumentForEmployerRequest>>
    >(employerConstants.UPLOAD_EMPLOYEES_DOCUMENT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      uploadEmployeesDocumentForEmployerRequest(
        client,
        groupId,
        file,
        companyId
      )
    );
  };
};

export const getPaginatedCompanies = (
  client: ApolloClient<object>,
  params: Parameters<typeof getCompaniesSet>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getCompaniesSet>>
    >(adminConstants.GET_PAGINATED_COMPANIES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getCompaniesSet(client, params)
    );
  };
};

export const linkCompanyBank = (
  client: ApolloClient<object>,
  params: Parameters<typeof linkBank>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof linkBank>>
    >(adminConstants.LINK_BANK);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      linkBank(client, params)
    );
  };
};

export const updateCompanyKybInfo = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateCompanyKYBInfo>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateCompanyKYBInfo>>
    >(adminConstants.REGISTER_KYB_COMPANY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateCompanyKYBInfo(client, params)
    );
  };
};

export const updateCompanyInfo = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateCompanyInfoNonKyb>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateCompanyInfoNonKyb>>
    >(adminConstants.UPDATE_COMPANY_INFO);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateCompanyInfoNonKyb(client, params)
    );
  };
};

export const createOrganizationForCompany = (
  client: ApolloClient<object>,
  params: Parameters<typeof createOrganization>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof createOrganization>>
    >(adminConstants.CREATE_ORGANIZATION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      createOrganization(client, params)
    );
  };
};

export const getOrganizationForCompany = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof getOrgForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getOrgForCompany>>
    >(adminConstants.GET_ORGANIZATION_FOR_COMPANY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getOrgForCompany(client, companyId)
    );
  };
};

export const disableCompany = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof disableCompanyRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof disableCompanyRequest>>
    >(adminConstants.DISABLE_COMPANY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      disableCompanyRequest(client, companyId)
    );
  };
};

export const enableCompany = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof enableCompanyRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof enableCompanyRequest>>
    >(adminConstants.ENABLE_COMPANY);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      enableCompanyRequest(client, companyId)
    );
  };
};

export const submitBusinessDocuments = (
  client: ApolloClient<object>,
  params: Parameters<typeof submitKYBBusinessDocuments>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitKYBBusinessDocuments>>
    >(adminConstants.SUBMIT_BUSINESS_KYB_DOCUMENTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitKYBBusinessDocuments(client, params)
    );
  };
};

export const submitBusinessMemberDocuments = (
  client: ApolloClient<object>,
  params: Parameters<typeof submitKYBMemberDocuments>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof submitKYBMemberDocuments>>
    >(adminConstants.SUBMIT_BUSINESS_MEMBERS_KYB_DOCUMENTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      submitKYBMemberDocuments(client, params)
    );
  };
};

export const getBanksAndGroups = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof getGroupsAndBanks>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getGroupsAndBanks>>
    >(adminConstants.GET_BANKS_AND_GROUPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getGroupsAndBanks(client, companyId)
    );
  };
};

export const getPaginatedPayrolls = (
  client: ApolloClient<object>,
  params: Parameters<typeof getPayrolls>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getPayrolls>>
    >(adminConstants.GET_PAYROLLS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getPayrolls(client, params)
    );
  };
};

export const getKybStatusAndInfo = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof getKybStatusForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getKybStatusForCompany>>
    >(adminConstants.GET_KYB_STATUS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getKybStatusForCompany(client, companyId)
    );
  };
};

export const searchAllUsers = (
  client: ApolloClient<object>,
  params: Parameters<typeof searchUsers>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof searchUsers>>
    >(adminConstants.SEARCH_USERS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      searchUsers(client, params)
    );
  };
};

export const getUser = (
  client: ApolloClient<object>,
  userId: Parameters<typeof getUserDetails>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getUserDetails>>
    >(adminConstants.GET_USER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getUserDetails(client, userId)
    );
  };
};

export const getUserAlloyApplication = (
  client: ApolloClient<object>,
  userId: Parameters<typeof getAlloyApplication>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getAlloyApplication>>
    >(adminConstants.GET_ALLOY_APPLICATION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getAlloyApplication(client, userId)
    );
  };
};

export const getTransactionsForUser = (
  client: ApolloClient<object>,
  userId: Parameters<typeof getUserTransactions>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getUserTransactions>>
    >(adminConstants.GET_USER_TRANSACTIONS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getUserTransactions(client, userId)
    );
  };
};

export const lockTargetUser = (
  client: ApolloClient<object>,
  userId: Parameters<typeof lockUser>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof lockUser>>
    >(adminConstants.LOCK_USER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      lockUser(client, userId)
    );
  };
};

export const unlockTargetUser = (
  client: ApolloClient<object>,
  userId: Parameters<typeof unlockUser>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof unlockUser>>
    >(adminConstants.UNLOCK_USER);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      unlockUser(client, userId)
    );
  };
};

export const requirePortfolioUpdate = (
  client: ApolloClient<object>,
  userId: Parameters<typeof requirePortfolioUpdateRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof requirePortfolioUpdateRequest>>
    >(adminConstants.REQUIRE_PORTFOLIO_UPDATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      requirePortfolioUpdateRequest(client, userId)
    );
  };
};

export const getPricingData = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getPricingSheet>>
    >(adminConstants.GET_PRICING_SHEET);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getPricingSheet(client)
    );
  };
};

export const getBillingDetails = (
  client: ApolloClient<object>,
  companyId: Parameters<typeof getBillingDetailsForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getBillingDetailsForCompany>>
    >(adminConstants.GET_BILLING_DETAILS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getBillingDetailsForCompany(client, companyId)
    );
  };
};

export const createInvoiceForCustomer = (
  client: ApolloClient<object>,
  params: Parameters<typeof createInvoiceForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof createInvoiceForCompany>>
    >(adminConstants.CREATE_BILLING_INVOICE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      createInvoiceForCompany(client, params)
    );
  };
};

export const getPendingLinkedEmployers = (
  client: ApolloClient<object>,
  userId: Parameters<typeof getPendingAndLinkedEmployers>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getPendingAndLinkedEmployers>>
    >(adminConstants.GET_PENDING_LINKED_EMPLOYERS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getPendingAndLinkedEmployers(client, userId)
    );
  };
};
export const liquidateAccount = (
  client: ApolloClient<object>,
  params: Parameters<typeof liquidateUserAccount>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof liquidateUserAccount>>
    >(adminConstants.LIQUIDATE_ACCOUNT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      liquidateUserAccount(client, params)
    );
  };
};

export const resetUserOnboardingState = (
  client: ApolloClient<object>,
  userId: Parameters<typeof resetUserState>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof resetUserState>>
    >(adminConstants.RESET_USER_STATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      resetUserState(client, userId)
    );
  };
};

export const disburseUserAccount = (
  client: ApolloClient<object>,
  params: Parameters<typeof partiallyDisburseUserAccount>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof partiallyDisburseUserAccount>>
    >(adminConstants.DISPERSE_USER_ACCOUNT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      partiallyDisburseUserAccount(client, params)
    );
  };
};

export const reopenUserAccountRequest = (
  client: ApolloClient<object>,
  params: Parameters<typeof reopenUserAccount>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof reopenUserAccount>>
    >(adminConstants.REOPEN_USER_ACCOUNT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      reopenUserAccount(client, params)
    );
  };
};

export const getEntityLimits = (
  client: ApolloClient<object>,
  entityType: Parameters<typeof getEntityLimitsRequest>[1],
  entityId: Parameters<typeof getEntityLimitsRequest>[2]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getEntityLimitsRequest>>
    >(adminConstants.GET_ENTITY_LIMITS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getEntityLimitsRequest(client, entityType, entityId)
    );
  };
};

export const updateEntityLimit = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateEntityLimitRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateEntityLimitRequest>>
    >(adminConstants.UPDATE_ENTITY_LIMIT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateEntityLimitRequest(client, params)
    );
  };
};

export const linkEmployerToCrm = (
  client: ApolloClient<object>,
  params: Parameters<typeof linkEmployerToCrmRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof linkEmployerToCrmRequest>>
    >(adminConstants.LINK_COMPANY_TO_CRM);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      linkEmployerToCrmRequest(client, params)
    );
  };
};

export const unlinkEmployerToCrm = (
  client: ApolloClient<object>,
  params: Parameters<typeof unlinkEmployerToCrmRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof unlinkEmployerToCrmRequest>>
    >(adminConstants.UNLINK_COMPANY_TO_CRM);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      unlinkEmployerToCrmRequest(client, params)
    );
  };
};

export const getEmployerCrmLink = (
  client: ApolloClient<object>,
  params: Parameters<typeof getEmployerCrmLinkRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getEmployerCrmLinkRequest>>
    >(adminConstants.GET_COMPANY_CRM_LINK);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getEmployerCrmLinkRequest(client, params)
    );
  };
};

export const syncCrm = (
  client: ApolloClient<object>,
  params: Parameters<typeof syncCrmRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof syncCrmRequest>>
    >(adminConstants.SYNC_CRM_DATA);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      syncCrmRequest(client, params)
    );
  };
};

export const getPaginatedMissedPayrolls = (
  client: ApolloClient<object>,
  params: Parameters<typeof getAllUnprocessedPayrollsRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getAllUnprocessedPayrollsRequest>>
    >(adminConstants.GET_UNPROCESSED_PAYROLLS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getAllUnprocessedPayrollsRequest(client, params)
    );
  };
};

export const changeBillingType = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateBillingType>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateBillingType>>
    >(adminConstants.CHANGE_BILLING_TYPES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateBillingType(client, params)
    );
  };
};

export const getPaginatedWorkflows = (
  client: ApolloClient<object>,
  params: Parameters<typeof getWorkflows>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getWorkflows>>
    >(adminConstants.GET_WORKFLOWS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getWorkflows(client, params)
    );
  };
};

export const restartWorkflow = (
  client: ApolloClient<object>,
  workflowId: Parameters<typeof restartWorkflowRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof restartWorkflowRequest>>
    >(adminConstants.RESTART_WORKFLOW);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      restartWorkflowRequest(client, workflowId)
    );
  };
};

export const getLastWorkflowStepForWorkflow = (
  client: ApolloClient<object>,
  params: Parameters<typeof getLastWorkflowStep>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getLastWorkflowStep>>
    >(adminConstants.GET_WORKFLOW_STEPS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getLastWorkflowStep(client, params)
    );
  };
};

export const getPaginatedCustomReports = (
  client: ApolloClient<object>,
  params: Parameters<typeof getCustomReports>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getCustomReports>>
    >(adminConstants.GET_CUSTOM_REPORTS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getCustomReports(client, params)
    );
  };
};

export const getCustomReportTypes = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getReportTypes>>
    >(adminConstants.GET_CUSTOM_REPORT_TYPES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getReportTypes(client)
    );
  };
};

export const createCustomReport = (
  client: ApolloClient<object>,
  params: Parameters<typeof createCustomReportRequest>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof createCustomReportRequest>>
    >(adminConstants.CREATE_CUSTOM_REPORT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      createCustomReportRequest(client, params)
    );
  };
};

export const updateBillingEmail = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateBillingContact>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateBillingContact>>
    >(adminConstants.UPDATE_BILLING_CONTACT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateBillingContact(client, params)
    );
  };
};

export const approvePayrollIntegration = (
  client: ApolloClient<object>,
  params: Parameters<typeof approvePayrollIntegrationSetup>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof approvePayrollIntegrationSetup>>
    >(adminConstants.APPROVE_PAYROLL_INTEGRATION);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      approvePayrollIntegrationSetup(client, params)
    );
  };
};

export const getKycStatus = (
  client: ApolloClient<object>,
  params: Parameters<typeof getKybStatus>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getKybStatus>>
    >(adminConstants.GET_KYC_STATUS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getKybStatus(client, params)
    );
  };
};

export const getReportsStatus = (
  client: ApolloClient<object>,
  params: Parameters<typeof getReportsStatuses>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getReportsStatuses>>
    >(adminConstants.GET_REPORTS_STATUS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getReportsStatuses(client, params)
    );
  };
};

export const migrateEmployees = (
  client: ApolloClient<object>,
  params: Parameters<typeof migrateEmployeesForCompany>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof migrateEmployeesForCompany>>
    >(adminConstants.ADMIN_MIGRATE_EMPLOYEES);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      migrateEmployeesForCompany(client, params)
    );
  };
};

// searches fire as the admin types, so only the latest request's response is applied
let latestEmployerOrdersRequest = 0;

export const getPaginatedEmployerOrders = (
  client: ApolloClient<object>,
  params: Parameters<typeof getEmployerOrders>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getEmployerOrders>>
    >(adminConstants.GET_EMPLOYER_ORDERS);
    const requestId = ++latestEmployerOrdersRequest;
    const isLatest = () => requestId === latestEmployerOrdersRequest;

    dispatch(actionCreator.request());
    return getEmployerOrders(client, params).then(
      (data) =>
        isLatest() ? dispatch(actionCreator.success(data)) : undefined,
      (error) =>
        isLatest() ? dispatch(actionCreator.failure(error)) : undefined
    );
  };
};

export const getUserRollovers = (
  client: ApolloClient<object>,
  userId: Parameters<typeof userRollovers>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof userRollovers>>
    >(adminConstants.GET_USER_ROLLOVERS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      userRollovers(client, userId)
    );
  };
};

export const getPaychexUsersReport = (
  client: ApolloClient<object>,
  userIds: Parameters<typeof paychexUsersReport>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof paychexUsersReport>>
    >(adminConstants.GET_PAYCHEX_USERS_REPORT);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      paychexUsersReport(client, userIds)
    );
  };
};

export const getAllPaychexUsers = (client: ApolloClient<object>) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof getPaychexUsers>>
    >(adminConstants.GET_PAYCHEX_USERS);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      getPaychexUsers(client)
    );
  };
};

export const submitBusinessRegistrationUpdate = (
  client: ApolloClient<object>,
  params: Parameters<typeof requestBusinessRegistrationUpdate>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof requestBusinessRegistrationUpdate>>
    >(adminConstants.REQUEST_BUSINESS_REGISTRATION_UPDATE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      requestBusinessRegistrationUpdate(client, params)
    );
  };
};

export const updateUserEmail = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateUserEmailService>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateUserEmailService>>
    >(adminConstants.UPDATE_USER_EMAIL);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateUserEmailService(client, params)
    );
  };
};

export const updateUserKyc = (
  client: ApolloClient<object>,
  params: Parameters<typeof updateUserKycService>[1]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof updateUserKycService>>
    >(adminConstants.UPDATE_USER_KYC);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      updateUserKycService(client, params)
    );
  };
};

export const adminUpdateUserProfile = (
  client: ApolloClient<object>,
  userId: Parameters<typeof adminUpdateUserProfileService>[1]["userId"],
  profileParams: Parameters<
    typeof adminUpdateUserProfileService
  >[1]["profileParams"]
) => {
  return (dispatch: Dispatch) => {
    const actionCreator = new ActionCreator<
      Awaited<ReturnType<typeof adminUpdateUserProfileService>>
    >(adminConstants.ADMIN_UPDATE_USER_PROFILE);
    return actionCreator.dispatchFullLifecycle(
      dispatch,
      adminUpdateUserProfileService(client, { userId, profileParams })
    );
  };
};
